import React, { useState } from 'react';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';

const RestaurantListingsTab = () => {
  const [selectedRestaurant, setSelectedRestaurant] = useState(null);
  const [filter, setFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');

  const restaurantListings = [
    {
      id: 1,
      name: 'Spice Garden Restaurant',
      owner: 'Raj Kumar',
      cuisine: 'North Indian',
      submissionDate: '2024-10-26',
      status: 'pending',
      location: 'Connaught Place, Delhi',
      thumbnail: 'https://images.unsplash.com/photo-1517248135467-4c7edcad34c4?w=300&h=200',
      documents: ['FSSAI License', 'Fire Safety Certificate', 'Trade License'],
      complianceScore: 78,
      flaggedIssues: ['FSSAI license expires soon'],
      rating: 4.2,
      averagePrice: '₹800 for two'
    },
    {
      id: 2,
      name: 'Ocean View Seafood',
      owner: 'Marina Restaurants',
      cuisine: 'Seafood',
      submissionDate: '2024-10-25',
      status: 'under_review',
      location: 'Bandra West, Mumbai',
      thumbnail: 'https://images.unsplash.com/photo-1559339352-11d035aa65de?w=300&h=200',
      documents: ['FSSAI License', 'Liquor License', 'Health Certificate'],
      complianceScore: 95,
      flaggedIssues: [],
      rating: 4.6,
      averagePrice: '₹1500 for two'
    },
    {
      id: 3,
      name: 'Cafe Mocha',
      owner: 'Coffee Culture Pvt Ltd',
      cuisine: 'Continental',
      submissionDate: '2024-10-24',
      status: 'approved',
      location: 'Koramangala, Bangalore',
      thumbnail: 'https://images.unsplash.com/photo-1554118811-1e0d58224f24?w=300&h=200',
      documents: ['FSSAI License', 'Music License', 'Trade License'],
      complianceScore: 92,
      flaggedIssues: [],
      rating: 4.4,
      averagePrice: '₹600 for two'
    }
  ];

  const statusColumns = [
    { id: 'pending', label: 'Pending Review', color: 'yellow' },
    { id: 'under_review', label: 'Under Verification', color: 'blue' },
    { id: 'approved', label: 'Approved', color: 'green' },
    { id: 'rejected', label: 'Rejected', color: 'red' }
  ];

  const filteredRestaurants = restaurantListings?.filter(restaurant => {
    if (filter !== 'all' && restaurant?.status !== filter) return false;
    if (searchQuery && !restaurant?.name?.toLowerCase()?.includes(searchQuery?.toLowerCase()) &&
        !restaurant?.owner?.toLowerCase()?.includes(searchQuery?.toLowerCase())) return false;
    return true;
  });

  const getRestaurantsByStatus = (status) => {
    return filteredRestaurants?.filter(restaurant => restaurant?.status === status);
  };

  const handleApprove = (restaurant) => {
    console.log('Approving restaurant:', restaurant?.id);
  };

  const handleReject = (restaurant) => {
    console.log('Rejecting restaurant:', restaurant?.id);
  };

  return (
    <div className="flex h-full">
      {/* Main Content */}
      <div className={`transition-all duration-300 ${selectedRestaurant ? 'flex-1' : 'w-full'}`}>
        {/* Filters */}
        <div className="p-6 border-b border-border bg-card">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1">
              <Input
                placeholder="Search restaurants or owners..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e?.target?.value)}
                className="w-full"
              />
            </div>
            <Select
              value={filter}
              onValueChange={setFilter}
              className="w-full sm:w-48"
            >
              <option value="all">All Status</option>
              <option value="pending">Pending</option>
              <option value="under_review">Under Review</option>
              <option value="approved">Approved</option>
              <option value="rejected">Rejected</option>
            </Select>
            <div className="flex gap-2">
              <Button variant="outline" size="sm">
                <Icon name="Filter" size={16} />
              </Button>
              <Button variant="outline" size="sm">
                Bulk Actions
              </Button>
            </div>
          </div>
        </div>

        {/* Kanban Board */}
        <div className="flex-1 p-6 overflow-auto">
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-6 h-full">
            {statusColumns?.map((column) => (
              <div
                key={column?.id}
                className="bg-muted/30 rounded-lg p-4 h-full"
              >
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-medium text-foreground">{column?.label}</h3>
                  <span className={`px-2 py-1 rounded text-xs font-medium bg-${column?.color}-100 text-${column?.color}-800`}>
                    {getRestaurantsByStatus(column?.id)?.length}
                  </span>
                </div>

                <div className="space-y-3">
                  {getRestaurantsByStatus(column?.id)?.map((restaurant) => (
                    <div
                      key={restaurant?.id}
                      onClick={() => setSelectedRestaurant(restaurant)}
                      className="bg-card border border-border rounded-lg p-4 cursor-pointer hover:shadow-md transition-shadow"
                    >
                      <div className="flex items-start gap-3">
                        <img
                          src={restaurant?.thumbnail}
                          alt={restaurant?.name}
                          className="w-12 h-12 rounded object-cover flex-shrink-0"
                        />
                        <div className="flex-1 min-w-0">
                          <h4 className="font-medium text-sm text-foreground truncate">
                            {restaurant?.name}
                          </h4>
                          <p className="text-xs text-muted-foreground truncate">
                            {restaurant?.owner}
                          </p>
                          <div className="flex items-center gap-2 mt-1">
                            <span className="text-xs text-primary font-medium">
                              {restaurant?.cuisine}
                            </span>
                            <div className="flex items-center gap-1">
                              <Icon name="Star" size={10} className="text-yellow-500 fill-current" />
                              <span className="text-xs text-muted-foreground">
                                {restaurant?.rating}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Price and Location */}
                      <div className="mt-3 flex items-center justify-between text-xs text-muted-foreground">
                        <span>{restaurant?.averagePrice}</span>
                        <span className="truncate ml-2">{restaurant?.location}</span>
                      </div>

                      {/* Compliance Score */}
                      <div className="mt-2 flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <div className={`w-2 h-2 rounded-full ${
                            restaurant?.complianceScore >= 90 ? 'bg-green-500' :
                            restaurant?.complianceScore >= 70 ? 'bg-yellow-500' : 'bg-red-500'
                          }`} />
                          <span className="text-xs text-muted-foreground">
                            {restaurant?.complianceScore}% compliant
                          </span>
                        </div>
                        <span className="text-xs text-muted-foreground">
                          {restaurant?.submissionDate}
                        </span>
                      </div>

                      {/* Quick Actions */}
                      {restaurant?.status === 'pending' && (
                        <div className="mt-3 flex gap-2">
                          <Button
                            size="sm"
                            className="flex-1 h-8 text-xs"
                            onClick={(e) => {
                              e?.stopPropagation();
                              handleApprove(restaurant);
                            }}
                          >
                            <Icon name="Check" size={12} />
                            Approve
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            className="flex-1 h-8 text-xs"
                            onClick={(e) => {
                              e?.stopPropagation();
                              handleReject(restaurant);
                            }}
                          >
                            <Icon name="X" size={12} />
                            Reject
                          </Button>
                        </div>
                      )}

                      {/* Flagged Issues */}
                      {restaurant?.flaggedIssues?.length > 0 && (
                        <div className="mt-2 p-2 bg-red-50 border border-red-200 rounded">
                          <div className="flex items-center gap-1">
                            <Icon name="AlertTriangle" size={12} className="text-red-500" />
                            <span className="text-xs text-red-700">
                              {restaurant?.flaggedIssues?.[0]}
                            </span>
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
      {/* Detail Panel */}
      {selectedRestaurant && (
        <div className="w-96 bg-card border-l border-border p-6 overflow-auto">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-foreground">Restaurant Details</h3>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setSelectedRestaurant(null)}
            >
              <Icon name="X" size={16} />
            </Button>
          </div>

          <div className="space-y-6">
            {/* Restaurant Info */}
            <div>
              <img
                src={selectedRestaurant?.thumbnail}
                alt={selectedRestaurant?.name}
                className="w-full h-32 object-cover rounded-lg"
              />
              <div className="mt-3">
                <h4 className="font-medium text-foreground">
                  {selectedRestaurant?.name}
                </h4>
                <p className="text-sm text-muted-foreground">
                  Owner: {selectedRestaurant?.owner}
                </p>
                <div className="flex items-center gap-4 mt-2">
                  <span className="text-sm text-primary font-medium">
                    {selectedRestaurant?.cuisine}
                  </span>
                  <div className="flex items-center gap-1">
                    <Icon name="Star" size={14} className="text-yellow-500 fill-current" />
                    <span className="text-sm font-medium">
                      {selectedRestaurant?.rating}
                    </span>
                  </div>
                </div>
                <p className="text-sm text-muted-foreground mt-1">
                  {selectedRestaurant?.location}
                </p>
                <p className="text-sm font-medium text-foreground mt-1">
                  {selectedRestaurant?.averagePrice}
                </p>
              </div>
            </div>

            {/* License & Compliance */}
            <div>
              <h5 className="font-medium text-foreground mb-3">License & Compliance</h5>
              <div className="space-y-2">
                {selectedRestaurant?.documents?.map((doc, index) => (
                  <div key={index} className="flex items-center justify-between">
                    <span className="text-sm text-foreground">{doc}</span>
                    <Icon name="CheckCircle" size={16} className="text-green-500" />
                  </div>
                ))}
              </div>
              
              {/* Compliance Score */}
              <div className="mt-4 p-3 bg-muted rounded-lg">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium">Compliance Score</span>
                  <span className="text-lg font-bold text-primary">
                    {selectedRestaurant?.complianceScore}%
                  </span>
                </div>
                <div className="w-full bg-border rounded-full h-2 mt-2">
                  <div
                    className={`h-2 rounded-full ${
                      selectedRestaurant?.complianceScore >= 90 ? 'bg-green-500' :
                      selectedRestaurant?.complianceScore >= 70 ? 'bg-yellow-500' : 'bg-red-500'
                    }`}
                    style={{ width: `${selectedRestaurant?.complianceScore}%` }}
                  />
                </div>
              </div>
            </div>

            {/* Flagged Issues */}
            {selectedRestaurant?.flaggedIssues?.length > 0 && (
              <div>
                <h5 className="font-medium text-foreground mb-3">Flagged Issues</h5>
                <div className="space-y-2">
                  {selectedRestaurant?.flaggedIssues?.map((issue, index) => (
                    <div key={index} className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded">
                      <Icon name="AlertTriangle" size={14} className="text-red-500 mt-0.5 flex-shrink-0" />
                      <span className="text-sm text-red-700">{issue}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Approval History */}
            <div>
              <h5 className="font-medium text-foreground mb-3">Submission History</h5>
              <div className="text-sm text-muted-foreground">
                <div className="flex items-center gap-2 mb-2">
                  <Icon name="Calendar" size={12} />
                  Submitted on {selectedRestaurant?.submissionDate}
                </div>
                <div className="flex items-center gap-2">
                  <Icon name="User" size={12} />
                  Assigned to Food Safety Team
                </div>
              </div>
            </div>

            {/* Actions */}
            <div className="space-y-3">
              <Button className="w-full">
                <Icon name="Check" size={16} />
                Approve Restaurant
              </Button>
              <Button variant="outline" className="w-full">
                <Icon name="X" size={16} />
                Reject Application
              </Button>
              <Button variant="outline" className="w-full">
                <Icon name="MessageCircle" size={16} />
                Request Documents
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RestaurantListingsTab;